import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Menu, LogOut, CreditCard, Shield, Moon, Sun, Sparkles, User, GraduationCap, Coins } from "lucide-react"
import { useTheme } from "next-themes"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from 'react-i18next'
import { AuthModal } from "./AuthModal"
import { CreditPurchaseModal } from "./CreditPurchaseModal"
import { SubscriptionModal } from "./SubscriptionModal"
import { isAdmin } from "@/lib/auth"
import { useSubscription } from "@/hooks/useSubscription"
import type { User as SupabaseUser } from '@supabase/supabase-js'

export function HamburgerMenu() {
  const [user, setUser] = useState<SupabaseUser | null>(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [userIsAdmin, setUserIsAdmin] = useState(false)
  const [credits, setCredits] = useState<number | null>(null)
  const { toast } = useToast()
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const { subscription, hasActiveSubscription } = useSubscription()

  useEffect(() => {
    // Get current user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        checkAdminStatus()
        fetchCredits()
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        checkAdminStatus()
        fetchCredits()
      } else {
        setUserIsAdmin(false)
        setCredits(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchCredits = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-credits')
      if (!error && data) {
        setCredits(data.balance)
      }
    } catch (err) {
      console.error('Error fetching credits:', err)
    }
  }

  const checkAdminStatus = async () => {
    try {
      const adminStatus = await isAdmin()
      setUserIsAdmin(adminStatus)
    } catch (error) {
      console.error("Error checking admin status:", error)
      setUserIsAdmin(false)
    }
  }

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      toast({
        title: t('navigation.signOut'),
        description: t('auth.logoutSuccess')
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign out",
        variant: "destructive"
      })
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="p-2" aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? (
              <>
                <Sun className="mr-2 h-4 w-4" />
                Light Mode
              </>
            ) : (
              <>
                <Moon className="mr-2 h-4 w-4" />
                Dark Mode
              </>
            )}
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => navigate('/explore')}>
            <Sparkles className="mr-2 h-4 w-4" />
            Explore
          </DropdownMenuItem>

          {/* Tutorials - visible on mobile */}
          <DropdownMenuItem onClick={() => navigate('/tutorials')} className="md:hidden">
            <GraduationCap className="mr-2 h-4 w-4 text-emerald-600" />
            <span className="text-emerald-600">{t("tutorials.button", "Tutorials")}</span>
          </DropdownMenuItem>
          
          {user ? (
            <>
              {/* Credits/Subscription status - visible on mobile */}
              <div className="md:hidden px-2 py-1.5 text-sm">
                {hasActiveSubscription ? (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <Coins className="h-4 w-4" />
                      {credits?.toLocaleString() ?? '...'} credits
                    </span>
                    <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                      {subscription?.planName || 'Pro'}
                    </span>
                  </div>
                ) : (
                  <button 
                    onClick={() => setShowSubscriptionModal(true)}
                    className="w-full text-left text-primary flex items-center gap-2"
                  >
                    <Coins className="h-4 w-4" />
                    Subscribe for benefits
                  </button>
                )}
              </div>
              <DropdownMenuSeparator className="md:hidden" />
              
              <DropdownMenuSeparator className="hidden md:block" />
              <DropdownMenuItem onClick={() => navigate('/account')}>
                <User className="mr-2 h-4 w-4" />
                Account & Support
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => setShowCreditModal(true)}>
                <CreditCard className="mr-2 h-4 w-4" />
                {t('navigation.buyCredits')}
              </DropdownMenuItem>
              
              {userIsAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <Shield className="mr-2 h-4 w-4" />
                    {t('navigation.adminPanel')}
                  </DropdownMenuItem>
                </>
              )}
              
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t('navigation.signOut')}
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowAuthModal(true)}>
                {t('navigation.signIn')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => setShowAuthModal(false)}
      />

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal 
        open={showCreditModal}
        onOpenChange={setShowCreditModal}
        onPurchaseComplete={() => setShowCreditModal(false)}
      />

      {/* Subscription Modal */}
      <SubscriptionModal 
        open={showSubscriptionModal}
        onOpenChange={setShowSubscriptionModal}
      />
    </>
  )
}